import React from 'react';
import { SectionInstance } from '../types';

const DS: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'rgba(255,255,255,0.60)',
  margin: 0,
  paddingTop: 8,
  borderTop: '1px solid rgba(255,255,255,0.10)',
  lineHeight: 1.5,
};

const LINK_STYLE: React.CSSProperties = {
  color: 'var(--brand-accent, #c9a23b)',
  textDecoration: 'underline',
};

const BTN_PRIMARY: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6, border: 'none',
  background: 'var(--brand-primary, #2563eb)', color: '#fff',
  fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
};

const BTN_SECONDARY: React.CSSProperties = {
  padding: '8px 20px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.3)', background: 'transparent',
  color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
};

function Disclaimer({ text }: { text: string }) {
  return <p style={DS}>{text}</p>;
}

const ComplianceBanner: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;

  const message      = c.message      || 'We use cookies to improve your experience.';
  const acceptText   = c.accept_text  || 'Accept All';
  const declineText  = c.decline_text || 'Decline';
  const privacyUrl   = c.privacy_url  || '/privacy';
  const jurisdiction = c.jurisdiction || 'australia';
  const licenseHolder = c.license_holder || '';
  const showRisk     = c.show_risk_disclaimer ?? true;
  const riskText     = c.risk_disclaimer_text ||
    'All investments carry risk. Past performance is not a reliable indicator of future performance. You should obtain independent financial advice before making any investment decision.';

  const disclaimers: React.ReactNode[] = [];

  if (jurisdiction === 'australia') {
    const afsl = c.afsl_number || '';
    if (afsl) {
      const holderPart = licenseHolder ? ` | ${licenseHolder}` : '';
      disclaimers.push(<Disclaimer key="afsl" text={`AFSL: ${afsl}${holderPart}`} />);
    } else if (licenseHolder) {
      disclaimers.push(<Disclaimer key="holder" text={licenseHolder} />);
    }
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);
    if (c.show_wholesale_notice) {
      disclaimers.push(<Disclaimer key="wholesale" text={c.wholesale_notice_text ||
        'This information is provided to wholesale clients only as defined under section 761G of the Corporations Act 2001 (Cth).'} />);
    }
    if (c.show_past_performance) {
      disclaimers.push(<Disclaimer key="pastperf" text={c.past_performance_text ||
        'Past performance is not a reliable indicator of future performance and should not be relied upon when making investment decisions.'} />);
    }
    if (c.show_anti_hawking) {
      disclaimers.push(<Disclaimer key="hawking" text={c.anti_hawking_text ||
        'This document does not constitute an offer in any jurisdiction where, or to any person to whom, it would not be lawful to make such an offer.'} />);
    }

  } else if (jurisdiction === 'usa') {
    const ria = c.sec_ria_number || '';
    const finra = c.finra_crd || '';
    const regParts = [ria && `SEC/RIA CRD: ${ria}`, finra && `FINRA CRD: ${finra}`, licenseHolder].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);
    if (c.show_investment_advice_notice ?? true) {
      disclaimers.push(<Disclaimer key="advice" text={c.investment_advice_text ||
        'This material is for informational purposes only and does not constitute investment advice, a solicitation, or an offer to buy or sell any security. Past performance is not indicative of future results.'} />);
    }

  } else if (jurisdiction === 'uk') {
    const fca = c.fca_number || '';
    const regParts = [fca && `FCA Ref: ${fca}`, licenseHolder].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);
    if (c.show_past_performance) {
      disclaimers.push(<Disclaimer key="pastperf" text={c.past_performance_text ||
        'Past performance is not a reliable indicator of future performance and should not be relied upon when making investment decisions.'} />);
    }

  } else if (jurisdiction === 'eu') {
    const auth = c.mifid_auth_number || '';
    const nca  = c.nca_name || '';
    const regParts = [
      auth && `MiFID II Auth: ${auth}`,
      nca  && `Supervised by ${nca}`,
      licenseHolder,
    ].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);
    if (c.show_past_performance) {
      disclaimers.push(<Disclaimer key="pastperf" text={c.past_performance_text ||
        'Past performance is not a reliable indicator of future performance and should not be relied upon when making investment decisions.'} />);
    }

  } else if (jurisdiction === 'singapore') {
    const cms = c.mas_cms_number || '';
    const regParts = [cms && `MAS CMS: ${cms}`, licenseHolder].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);
    if (c.show_accredited_investor) {
      disclaimers.push(<Disclaimer key="acc" text={c.accredited_investor_text ||
        'This material is directed at accredited investors only as defined under the Securities and Futures Act 2001 (Singapore).'} />);
    }

  } else if (jurisdiction === 'hong_kong') {
    const sfcNum  = c.sfc_license_number || '';
    const sfcType = c.sfc_license_type || 'Type 1';
    const regLabel = sfcNum ? `SFC ${sfcType}: ${sfcNum}` : (sfcType ? `SFC ${sfcType}` : '');
    const regParts = [regLabel, licenseHolder].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);

  } else if (jurisdiction === 'japan') {
    const fsa = c.fsa_registration_number || '';
    const regParts = [fsa && `FSA Reg: ${fsa}`, licenseHolder].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);

  } else if (jurisdiction === 'india') {
    const sebi = c.sebi_registration_number || '';
    const regParts = [sebi && `SEBI Reg: ${sebi}`, licenseHolder].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);

  } else if (jurisdiction === 'south_korea') {
    const fsc = c.fsc_registration_number || '';
    const regParts = [fsc && `FSC Reg: ${fsc}`, licenseHolder].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);

  } else {
    // other
    const lic  = c.other_license_number || '';
    const body = c.regulatory_body || '';
    const regParts = [
      lic  && `Licence: ${lic}`,
      body && `Regulated by ${body}`,
      licenseHolder,
    ].filter(Boolean);
    if (regParts.length) disclaimers.push(<Disclaimer key="reg" text={regParts.join(' | ')} />);
    if (showRisk) disclaimers.push(<Disclaimer key="risk" text={riskText} />);
  }

  return (
    <aside style={{
      background: '#1e293b', color: '#fff',
      padding: '16px 24px', display: 'flex',
      alignItems: 'flex-start', gap: 16, flexWrap: 'wrap',
    }}>
      <p style={{ flex: 1, margin: 0, fontSize: '0.875rem', minWidth: 200 }}>
        {message}{' '}
        <a href={privacyUrl} style={LINK_STYLE}>Privacy Policy</a>
      </p>

      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button className="sb-cta-btn" style={BTN_PRIMARY}>{acceptText}</button>
        <button style={BTN_SECONDARY}>{declineText}</button>
      </div>

      {disclaimers.length > 0 && (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {disclaimers}
        </div>
      )}
    </aside>
  );
};

export default ComplianceBanner;
